import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControl,
	FormHelperText,
	FormLabel,
	Stack,
	TextField,
	Typography,
	useTheme,
} from '@mui/material';
import { Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DataGrid } from '@mui/x-data-grid';
import { PageHeader } from '@/components/layouts/PageHeader';
import { FolioDrawer } from '@/components/Drawers/FolioDrawer';
import { DocumentTemplatesIcon } from '@/components/icons/DocumentTemplatesIcon';
import { paths } from '@/app/routes';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { OwnerCellData } from '@/components/tables/OwnerCell';
import {
	useCreateUnlayerDocumentTemplateMutation,
	useDeleteUnlayerDocumentTemplateMutation,
	useLazyGetUnlayerDocumentTemplateDetailQuery,
	useLazyGetUnlayerDocumentTemplateSummariesQuery,
	useUpdateUnlayerDocumentTemplateMutation,
} from '@/features/unlayerDocumentTemplates/unlayerDocumentTemplatesApi';
import { UnlayerEditorWrapper } from '@/components/UnlayerEditorWrapper';
import { getUnlayerTags } from '@/utils/unlayer';
import { formatDate } from '@/utils/constants';
import { showToast } from '@/features/toast/toastSlice';
import { InstantClientSelect } from '@/components/selects/ClientSelect';
import { InstantDealSelect } from '@/components/selects/DealSelect';
import { NoRowsOverlay } from '@/components/table/DataGridComponents/NoRowsOverlay';
import DataGridColumnMenu from '@/components/table/DataGridComponents/DataGridColumnMenu';
import DataGridColumnReorderToolbar from '@/components/table/DataGridComponents/DataGridColumnReorderToolbar';
import {
	ColumnControlContextProvider,
	useDataGridColumnProps,
} from '@/context/columnControlContext';

const UNLAYER_EDITOR_MIN_HEIGHT = 760;
const CREATE_NEW_SENTINEL = '__create_new__';

const compareStrings = (a, b) =>
	String(a ?? '').localeCompare(String(b ?? ''), undefined, { sensitivity: 'base' });

const getSortValue = (row, field) => {
	if (field === 'name') return row?.name ?? '';
	if (field === 'createdWhen') return Number(row?.createdWhen ?? 0);
	return Number(row?.lastUpdated ?? 0);
};

const getDesignFingerprint = (design) => {
	try {
		return JSON.stringify(design ?? null);
	} catch (error) {
		return '';
	}
};

const replaceMergeTagsInString = (value, mergeTags) => {
	if (typeof value !== 'string' || value.length === 0) return value;
	if (!mergeTags || typeof mergeTags !== 'object') return value;

	const collectMergeTagDefinitions = (tags) =>
		Object.values(tags).flatMap((tagDef) => {
			if (!tagDef || typeof tagDef !== 'object') return [];
			if (typeof tagDef.value === 'string') return [tagDef];
			if (tagDef.mergeTags && typeof tagDef.mergeTags === 'object') {
				return collectMergeTagDefinitions(tagDef.mergeTags);
			}
			return [];
		});

	return collectMergeTagDefinitions(mergeTags).reduce((nextValue, tagDef) => {
		const token = tagDef?.value;
		if (typeof token !== 'string' || token.length === 0) {
			return nextValue;
		}

		const replacement =
			tagDef?.sample === undefined || tagDef?.sample === null ? '' : String(tagDef.sample);
		return nextValue.split(token).join(replacement);
	}, value);
};

const replaceMergeTagsInDesign = (value, mergeTags) => {
	if (typeof value === 'string') {
		return replaceMergeTagsInString(value, mergeTags);
	}

	if (Array.isArray(value)) {
		return value.map((item) => replaceMergeTagsInDesign(item, mergeTags));
	}

	if (value && typeof value === 'object') {
		return Object.entries(value).reduce((acc, [key, nestedValue]) => {
			acc[key] = replaceMergeTagsInDesign(nestedValue, mergeTags);
			return acc;
		}, {});
	}

	return value;
};

const formatDateTime = (value) => {
	if (!value) return '-';
	try {
		return formatDate(value, true);
	} catch (error) {
		return '-';
	}
};

const buildUnlayerUserContext = ({ userDetails, fallbackEmail }) => ({
	name: userDetails?.name ?? '[User Name]',
	phoneNumber: userDetails?.phoneNumber ?? '',
	username: userDetails?.displayEmail ?? fallbackEmail,
	aboutUser: userDetails?.about ?? userDetails?.role ?? '',
	imageUrl: userDetails?.imageUrl ?? '',
	disclosureUrl: userDetails?.disclosureUrl ?? '',
	linkedIn: userDetails?.linkedIn ?? '',
	fspNumber: userDetails?.fspNumber ?? '',
	signature: userDetails?.signature ?? '',
	// Keep legacy aliases for existing callers while merge tags use the FIELDS names above.
	email: userDetails?.displayEmail ?? fallbackEmail,
	about: userDetails?.about ?? userDetails?.role ?? '',
	broker: userDetails?.broker,
});

export const UnlayerDocumentTemplatesView = () => (
	<ColumnControlContextProvider variant='unlayerDocumentTemplates' value='list'>
		<UnlayerDocumentTemplatesViewContent />
	</ColumnControlContextProvider>
);

const UnlayerDocumentTemplatesViewContent = () => {
	const dispatch = useDispatch();
	const theme = useTheme();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);
	const { getFeatureFlags, isLoading } = useFeatureFlagContext();

	const showUnlayerDocuments = Boolean(getFeatureFlags('unlayerDocuments')?.active);

	const userBrokerGroupIds = userDetails?.brokerGroupArray ?? [];

	const [sortField, setSortField] = useState('lastUpdated');
	const [sortDirection, setSortDirection] = useState('desc');
	const [selectedTemplateId, setSelectedTemplateId] = useState('');
	const [templateName, setTemplateName] = useState('');
	const [templateNameError, setTemplateNameError] = useState('');
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [isCreateMode, setIsCreateMode] = useState(false);
	const [designToLoad, setDesignToLoad] = useState(null);
	const [editorDesign, setEditorDesign] = useState(null);
	const [editorInstanceKey, setEditorInstanceKey] = useState(0);
	const [editorMinHeight, setEditorMinHeight] = useState(UNLAYER_EDITOR_MIN_HEIGHT);
	const [initialSnapshot, setInitialSnapshot] = useState(null);
	const [pendingTemplateId, setPendingTemplateId] = useState('');
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
	const [isPendingClose, setIsPendingClose] = useState(false);

	const [isTryDialogOpen, setIsTryDialogOpen] = useState(false);
	const [tryClient, setTryClient] = useState(null);
	const [tryDeal, setTryDeal] = useState(null);
	const [tryExportError, setTryExportError] = useState('');
	const [tryFallbackUrl, setTryFallbackUrl] = useState('');
	const [isExportingTryPdf, setIsExportingTryPdf] = useState(false);
	const [editorApi, setEditorApi] = useState(null);
	const [isLoadDesignJsonDialogOpen, setIsLoadDesignJsonDialogOpen] = useState(false);
	const [loadDesignJsonValue, setLoadDesignJsonValue] = useState('');
	const [loadDesignJsonError, setLoadDesignJsonError] = useState('');

	const [
		loadTemplateSummaries,
		{ data: templateSummaries = [], isFetching: isFetchingTemplates },
	] = useLazyGetUnlayerDocumentTemplateSummariesQuery();
	const [loadTemplateDetail, { isFetching: isFetchingTemplateDetail }] =
		useLazyGetUnlayerDocumentTemplateDetailQuery();
	const [createTemplate, { isLoading: isCreatingTemplate }] =
		useCreateUnlayerDocumentTemplateMutation();
	const [updateTemplate, { isLoading: isUpdatingTemplate }] =
		useUpdateUnlayerDocumentTemplateMutation();
	const [deleteTemplate, { isLoading: isDeletingTemplate }] =
		useDeleteUnlayerDocumentTemplateMutation();

	const mergeTags = useMemo(() => {
		const unlayerUserContext = buildUnlayerUserContext({
			userDetails,
			fallbackEmail: email,
		});
		return getUnlayerTags(unlayerUserContext, tryClient, tryDeal);
	}, [email, tryClient, tryDeal, userDetails]);

	const reloadTemplateSummaries = useCallback(async () => {
		if (!showUnlayerDocuments || userBrokerGroupIds.length === 0) return;
		await loadTemplateSummaries({ brokerGroupIds: userBrokerGroupIds });
	}, [loadTemplateSummaries, showUnlayerDocuments, userBrokerGroupIds]);

	useEffect(() => {
		reloadTemplateSummaries();
	}, [reloadTemplateSummaries]);

	useEffect(() => {
		const calculateEditorHeight = () =>
			setEditorMinHeight(Math.max(UNLAYER_EDITOR_MIN_HEIGHT, window.innerHeight - 220));
		calculateEditorHeight();
		window.addEventListener('resize', calculateEditorHeight);
		return () => window.removeEventListener('resize', calculateEditorHeight);
	}, []);

	const sortedTemplates = useMemo(() => {
		const rows = [...(templateSummaries ?? [])];
		return rows.sort((a, b) => {
			const aSortValue = getSortValue(a, sortField);
			const bSortValue = getSortValue(b, sortField);
			let result = 0;

			if (sortField === 'name') {
				result = compareStrings(aSortValue, bSortValue);
			} else {
				result = Number(aSortValue) - Number(bSortValue);
			}

			if (result === 0) {
				result = compareStrings(a?.name, b?.name);
			}

			return sortDirection === 'asc' ? result : result * -1;
		});
	}, [sortDirection, sortField, templateSummaries]);

	const currentFingerprint = getDesignFingerprint(editorDesign);
	const isDirty = Boolean(
		isDrawerOpen &&
		initialSnapshot &&
		(templateName.trim() !== initialSnapshot.name ||
			currentFingerprint !== initialSnapshot.fingerprint),
	);

	const clearTryDialogState = useCallback(() => {
		setIsTryDialogOpen(false);
		setTryClient(null);
		setTryDeal(null);
		setTryExportError('');
		setTryFallbackUrl('');
		setIsExportingTryPdf(false);
	}, []);

	const clearDrawerState = useCallback(() => {
		setIsDrawerOpen(false);
		setIsCreateMode(false);
		setSelectedTemplateId('');
		setTemplateName('');
		setTemplateNameError('');
		setDesignToLoad(null);
		setEditorDesign(null);
		setEditorInstanceKey((prev) => prev + 1);
		setInitialSnapshot(null);
		setPendingTemplateId('');
		setIsPendingClose(false);
		setIsLoadDesignJsonDialogOpen(false);
		setLoadDesignJsonValue('');
		setLoadDesignJsonError('');
		clearTryDialogState();
	}, [clearTryDialogState]);

	const openTemplateInDrawer = useCallback(
		async (templateId) => {
			const detail = await loadTemplateDetail({
				id: templateId,
				userId: userDetails?.userId,
				brokerGroupIds: userBrokerGroupIds,
			}).unwrap();
			const nextName = detail?.name ?? '';
			const nextDesign = detail?.designJson ?? null;

			setIsCreateMode(false);
			setSelectedTemplateId(templateId);
			setTemplateName(nextName);
			setTemplateNameError('');
			setDesignToLoad(nextDesign);
			setEditorDesign(nextDesign);
			setEditorInstanceKey((prev) => prev + 1);
			setInitialSnapshot({
				name: nextName.trim(),
				fingerprint: getDesignFingerprint(nextDesign),
			});
			setIsDrawerOpen(true);
		},
		[loadTemplateDetail, userBrokerGroupIds, userDetails?.userId],
	);

	const openCreateDrawer = useCallback(() => {
		setIsCreateMode(true);
		setSelectedTemplateId('');
		setTemplateName('');
		setTemplateNameError('');
		setDesignToLoad(null);
		setEditorDesign(null);
		setEditorInstanceKey((prev) => prev + 1);
		setInitialSnapshot({
			name: '',
			fingerprint: getDesignFingerprint(null),
		});
		setIsDrawerOpen(true);
	}, []);

	const requestOpenTemplate = useCallback(
		(templateId) => {
			if (isDirty) {
				setPendingTemplateId(templateId);
				setIsPendingClose(false);
				setIsDiscardDialogOpen(true);
				return;
			}
			openTemplateInDrawer(templateId).catch(() => {
				dispatch(
					showToast({
						message: 'Unable to load selected template.',
						error: true,
					}),
				);
			});
		},
		[dispatch, isDirty, openTemplateInDrawer],
	);

	const requestCreateTemplate = useCallback(() => {
		if (isDirty) {
			setPendingTemplateId(CREATE_NEW_SENTINEL);
			setIsPendingClose(false);
			setIsDiscardDialogOpen(true);
			return;
		}
		openCreateDrawer();
	}, [isDirty, openCreateDrawer]);

	const requestCloseDrawer = useCallback(() => {
		if (!isDrawerOpen) return;
		if (isDirty) {
			setIsPendingClose(true);
			setPendingTemplateId('');
			setIsDiscardDialogOpen(true);
			return;
		}
		clearDrawerState();
	}, [clearDrawerState, isDirty, isDrawerOpen]);

	const handleSaveTemplate = async () => {
		const trimmedName = templateName.trim();
		if (!trimmedName) {
			setTemplateNameError('Template name is required.');
			return;
		}

		if (!userDetails?.userId) {
			dispatch(
				showToast({
					message: 'Unable to save template. Missing user or template context.',
					error: true,
				}),
			);
			return;
		}

		try {
			if (isCreateMode || !selectedTemplateId) {
				const createdTemplate = await createTemplate({
					name: trimmedName,
					designJson: editorDesign ?? {},
					brokerGroupIds: userBrokerGroupIds,
				}).unwrap();
				setIsCreateMode(false);
				setSelectedTemplateId(createdTemplate?.id ?? '');
			} else {
				await updateTemplate({
					id: selectedTemplateId,
					name: trimmedName,
					designJson: editorDesign ?? {},
					brokerGroupIds: userBrokerGroupIds,
				}).unwrap();
			}
			await reloadTemplateSummaries();
			setInitialSnapshot({
				name: trimmedName,
				fingerprint: getDesignFingerprint(editorDesign),
			});
			dispatch(
				showToast({
					message: isCreateMode
						? 'Document template created.'
						: 'Document template saved.',
					autohide: true,
				}),
			);
		} catch (error) {
			dispatch(
				showToast({
					message: error?.data?.message ?? 'Unable to save document template.',
					error: true,
				}),
			);
		}
	};

	const handleDeleteTemplate = async () => {
		if (!selectedTemplateId) return;

		try {
			await deleteTemplate({
				id: selectedTemplateId,
				userId: userDetails?.userId,
				brokerGroupIds: userBrokerGroupIds,
			}).unwrap();
			setIsDeleteDialogOpen(false);
			clearDrawerState();
			await reloadTemplateSummaries();
			dispatch(
				showToast({
					message: 'Document template deleted.',
					autohide: true,
				}),
			);
		} catch (error) {
			dispatch(
				showToast({
					message: error?.data?.message ?? 'Unable to delete document template.',
					error: true,
				}),
			);
		}
	};

	const handleTryExportPdf = async () => {
		setTryExportError('');
		setTryFallbackUrl('');
		const unlayer = editorApi;
		if (!unlayer || typeof unlayer.exportPdf !== 'function') {
			setTryExportError('PDF export is unavailable for this editor session.');
			return;
		}

		if (typeof unlayer.setMergeTags === 'function') {
			unlayer.setMergeTags(mergeTags ?? {});
		}

		let currentDesign = null;
		let hasExportOverrides = false;
		const loadDesignAndWait = async (design) => {
			if (typeof unlayer.loadDesign !== 'function') return;
			await new Promise((resolve) => {
				let settled = false;
				const finalize = () => {
					if (settled) return;
					settled = true;
					if (typeof unlayer.removeEventListener === 'function') {
						unlayer.removeEventListener('design:loaded', finalize);
					}
					setTimeout(resolve, 0);
				};
				if (typeof unlayer.addEventListener === 'function') {
					unlayer.addEventListener('design:loaded', finalize);
				}
				unlayer.loadDesign(design);
				setTimeout(finalize, 300);
			});
		};

		setIsExportingTryPdf(true);
		try {
			currentDesign = await new Promise((resolve, reject) => {
				if (typeof unlayer.saveDesign !== 'function') {
					reject(new Error('Unable to read current template design.'));
					return;
				}
				unlayer.saveDesign((design) => {
					if (!design || typeof design !== 'object') {
						reject(new Error('Unable to read current template design.'));
						return;
					}
					resolve(design);
				});
			});

			const exportDesign = replaceMergeTagsInDesign(currentDesign, mergeTags ?? {});
			hasExportOverrides =
				getDesignFingerprint(exportDesign) !== getDesignFingerprint(currentDesign);

			if (hasExportOverrides) {
				await loadDesignAndWait(exportDesign);
			}

			await new Promise((resolve, reject) => {
				unlayer.exportPdf((data) => {
					if (data?.error) {
						reject(new Error(data.error));
						return;
					}
					const url = data?.url ?? data?.pdfUrl ?? data?.link ?? null;
					if (!url) {
						reject(new Error('No PDF URL returned from export.'));
						return;
					}
					const nextWindow = window.open(url, '_blank', 'noopener,noreferrer');
					if (!nextWindow) {
						setTryFallbackUrl(url);
					}
					resolve();
				});
			});
		} catch (error) {
			setTryExportError(error?.message ?? 'Unable to export PDF.');
		} finally {
			if (hasExportOverrides && currentDesign) {
				await loadDesignAndWait(currentDesign);
			}
			setIsExportingTryPdf(false);
		}
	};

	const handleLoadDesignJson = () => {
		let parsedDesign = null;
		try {
			parsedDesign = JSON.parse(loadDesignJsonValue);
		} catch (error) {
			setLoadDesignJsonError('Design JSON must be valid JSON.');
			return;
		}

		if (!parsedDesign || typeof parsedDesign !== 'object') {
			setLoadDesignJsonError('Design JSON must be a JSON object.');
			return;
		}

		setLoadDesignJsonError('');
		setDesignToLoad(parsedDesign);
		setIsLoadDesignJsonDialogOpen(false);
	};

	const handleDiscardConfirm = () => {
		setIsDiscardDialogOpen(false);
		const nextTemplateId = pendingTemplateId;
		const shouldClose = isPendingClose;
		setPendingTemplateId('');
		setIsPendingClose(false);

		if (shouldClose) {
			clearDrawerState();
			return;
		}

		if (nextTemplateId === CREATE_NEW_SENTINEL) {
			openCreateDrawer();
			return;
		}

		if (nextTemplateId) {
			openTemplateInDrawer(nextTemplateId).catch(() => {
				dispatch(
					showToast({
						message: 'Unable to load selected template.',
						error: true,
					}),
				);
			});
		}
	};

	const handleSort = (field) => {
		if (sortField === field) {
			setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
			return;
		}
		setSortField(field);
		setSortDirection(field === 'name' ? 'asc' : 'desc');
	};

	const sortModel = useMemo(
		() => [
			{
				field: sortField,
				sort: sortDirection,
			},
		],
		[sortField, sortDirection],
	);

	const columns = useMemo(
		() =>
			[
				{
					field: 'name',
					headerName: 'Template name',
					display: 'flex',
					flex: 2,
					minWidth: 200,
					hideable: false,
					renderCell: ({ row }) => row.name || '(Untitled template)',
				},
				{
					field: 'createdBy',
					headerName: 'Created by',
					display: 'flex',
					flex: 1,
					minWidth: 150,
					sortable: false,
					renderCell: ({ row }) => {
						const creatorId = row?.ownerDocId;
						if (creatorId == null) {
							return 'Unknown';
						}

					return <OwnerCellData userId={creatorId} color='text.primary' />;
				},
			},
			{
				field: 'createdWhen',
				headerName: 'Created date',
				display: 'flex',
				minWidth: 150,
				renderCell: ({ row }) => formatDateTime(row.createdWhen),
			},
			{
				field: 'lastUpdated',
				headerName: 'Last updated',
				display: 'flex',
				minWidth: 150,
				renderCell: ({ row }) => formatDateTime(row.lastUpdated),
			},
		],
		[],
	);

	const {
		columns: orderedColumns,
		columnVisibilityModel,
		onColumnVisibilityModelChange,
		onColumnWidthChange,
	} = useDataGridColumnProps(columns);

	if (!isLoading && !showUnlayerDocuments) {
		return <Navigate to={paths.home} replace />;
	}

	return (
		<Box
			sx={{
				pt: 1,
			}}
		>
			<PageHeader
				title='Document Templates'
				icon={
					<DocumentTemplatesIcon
						{...(theme.palette.mode === 'dark' && { bgColor: 'transparent' })}
					/>
				}
				headerElement={
					<Button variant='contained' onClick={requestCreateTemplate}>
						Create New
					</Button>
				}
			/>

			<Box px={3} pb={3}>
				<Box
					position='relative'
					height='75vh'
					sx={{
						border: `1px solid ${theme.palette.divider}`,
						borderRadius: '8px',
						backgroundColor: theme.palette.background.paper,
					}}
				>
					<DataGrid
						sx={{
							'--DataGrid-overlayHeight': '3em',
							'& .MuiDataGrid-cell': {
								padding: 1,
								minHeight: '42px',
								cursor: 'pointer',
							},
							'& .MuiDataGrid-row.Mui-selected': {
								color: 'unset',
							},
							border: 'none',
							borderRadius: '8px',
						}}
						loading={isFetchingTemplates && sortedTemplates.length === 0}
						slotProps={{
							loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' },
						}}
						rows={sortedTemplates}
						columns={orderedColumns}
						columnVisibilityModel={columnVisibilityModel}
						onColumnVisibilityModelChange={onColumnVisibilityModelChange}
						onColumnWidthChange={onColumnWidthChange}
						width='100%'
						getRowHeight={() => 'auto'}
						disableRowSelectionOnClick
						onRowClick={({ row }) => requestOpenTemplate(row.id)}
						sortingOrder={['desc', 'asc']}
						sortModel={sortModel}
						onSortModelChange={(newModel) => {
							const field = newModel?.[0]?.field;
							if (field) {
								handleSort(field);
							}
						}}
						slots={{
							noRowsOverlay: () => (
								<NoRowsOverlay
									label={
										isFetchingTemplates
											? 'Loading templates...'
											: 'No document templates found.'
									}
								/>
							),
							toolbar: DataGridColumnReorderToolbar,
							columnMenu: DataGridColumnMenu,
						}}
						disableColumnFilter
					/>
				</Box>
			</Box>

			<FolioDrawer
				open={isDrawerOpen}
				onClose={requestCloseDrawer}
				title={isCreateMode ? 'Create Document Template' : 'Edit Document Template'}
				canMaximise
				maximisedSize='full'
			>
				<Stack sx={{ flex: 1, gap: 2, pb: 2 }}>
					<TextField
						label='Template Name'
						value={templateName}
						onChange={(event) => {
							setTemplateName(event.target.value);
							if (templateNameError) {
								setTemplateNameError('');
							}
						}}
						error={Boolean(templateNameError)}
						helperText={templateNameError || ' '}
						fullWidth
					/>

					<Stack direction='row' spacing={1}>
						<Button
							variant='contained'
							disabled={
								isFetchingTemplateDetail ||
								isCreatingTemplate ||
								isUpdatingTemplate ||
								isDeletingTemplate
							}
							onClick={handleSaveTemplate}
						>
							{isCreatingTemplate || isUpdatingTemplate ? (
								<CircularProgress size={18} />
							) : isCreateMode ? (
								'Create'
							) : (
								'Save'
							)}
						</Button>
						<Button
							variant='outlined'
							color='error'
							disabled={
								isCreateMode ||
								isFetchingTemplateDetail ||
								isCreatingTemplate ||
								isUpdatingTemplate ||
								isDeletingTemplate
							}
							onClick={() => setIsDeleteDialogOpen(true)}
						>
							Delete
						</Button>
						<Button
							variant='outlined'
							disabled={isFetchingTemplateDetail || isDeletingTemplate}
							onClick={() => {
								setTryExportError('');
								setTryFallbackUrl('');
								setIsTryDialogOpen(true);
							}}
						>
							Try it out
						</Button>
					</Stack>

					<Stack direction='row' justifyContent='flex-end' sx={{ mt: 1 }}>
						<Button
							variant='outlined'
							disabled={
								isFetchingTemplateDetail ||
								isCreatingTemplate ||
								isUpdatingTemplate ||
								isDeletingTemplate
							}
							onClick={() => {
								setLoadDesignJsonError('');
								setIsLoadDesignJsonDialogOpen(true);
							}}
						>
							Load Design JSON
						</Button>
					</Stack>

					<Box sx={{ flex: 1, minHeight: 0 }}>
						<UnlayerEditorWrapper
							key={editorInstanceKey}
							minHeight={editorMinHeight}
							initialDesign={null}
							designToLoad={designToLoad}
							mergeTags={mergeTags}
							displayMode='document'
							onEditorReady={setEditorApi}
							onChange={(payload) => {
								setEditorDesign(payload?.design ?? null);
							}}
						/>
					</Box>
				</Stack>
			</FolioDrawer>

			<Dialog open={isTryDialogOpen} onClose={clearTryDialogState} fullWidth maxWidth='sm'>
				<DialogTitle>Try it out</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ mt: 1 }}>
						<Typography variant='body2' color='text.secondary'>
							{templateName.trim() || 'Untitled template'}
						</Typography>
						<FormControl fullWidth>
							<FormLabel>{'Client (Optional)'}</FormLabel>
							<InstantClientSelect
								clientId={tryClient?.clientId ?? null}
								clientName={tryClient?.name}
								setClient={(value) => {
									setTryClient(value ?? null);
									setTryDeal(null);
								}}
								width='100%'
							/>
						</FormControl>
						<FormControl fullWidth>
							<FormLabel>{'Deal (Optional)'}</FormLabel>
							<InstantDealSelect
								disabled={!tryClient?.clientId}
								clientId={tryClient?.clientId ?? null}
								dealId={tryDeal?.dealId ?? null}
								setDeal={(value) => setTryDeal(value ?? null)}
								autoPopulate={true}
							/>
						</FormControl>
						<Typography variant='caption' color='text.secondary'>
							Optional: pick a client/deal to populate merge tags. Otherwise sample
							values will be used.
						</Typography>
						<Typography variant='caption' color='text.secondary'>
							Export uses the current in-editor state, including unsaved changes.
						</Typography>
						{tryExportError ? (
							<FormHelperText error>{tryExportError}</FormHelperText>
						) : null}
						{tryFallbackUrl ? (
							<Typography variant='body2'>
								Popup blocked. Open PDF:{' '}
								<a href={tryFallbackUrl} target='_blank' rel='noreferrer'>
									{tryFallbackUrl}
								</a>
							</Typography>
						) : null}
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={clearTryDialogState}>Close</Button>
					<Button
						variant='contained'
						onClick={handleTryExportPdf}
						disabled={isExportingTryPdf}
					>
						{isExportingTryPdf ? <CircularProgress size={18} /> : 'Export to PDF'}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={isLoadDesignJsonDialogOpen}
				onClose={() => setIsLoadDesignJsonDialogOpen(false)}
				fullWidth
				maxWidth='md'
			>
				<DialogTitle>Load Design JSON</DialogTitle>
				<DialogContent>
					<TextField
						fullWidth
						multiline
						minRows={10}
						label='Full Unlayer design json'
						value={loadDesignJsonValue}
						onChange={(event) => {
							setLoadDesignJsonValue(event.target.value);
							if (loadDesignJsonError) {
								setLoadDesignJsonError('');
							}
						}}
						error={Boolean(loadDesignJsonError)}
						helperText={loadDesignJsonError || ' '}
						sx={{ mt: 1 }}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setIsLoadDesignJsonDialogOpen(false)}>Cancel</Button>
					<Button variant='contained' onClick={handleLoadDesignJson}>
						Load
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={isDeleteDialogOpen}
				onClose={() => setIsDeleteDialogOpen(false)}
				fullWidth
				maxWidth='sm'
			>
				<DialogTitle>Delete Template</DialogTitle>
				<DialogContent>
					<DialogContentText>Deleting this template cannot be undone.</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
					<Button
						variant='contained'
						color='error'
						onClick={handleDeleteTemplate}
						disabled={isDeletingTemplate}
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={isDiscardDialogOpen}
				onClose={() => setIsDiscardDialogOpen(false)}
				fullWidth
				maxWidth='sm'
			>
				<DialogTitle>Discard Unsaved Changes?</DialogTitle>
				<DialogContent>
					<DialogContentText>
						You have unsaved changes. If you continue, those changes will be lost.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setIsDiscardDialogOpen(false)}>Cancel</Button>
					<Button variant='contained' onClick={handleDiscardConfirm}>
						Discard Changes
					</Button>
				</DialogActions>
			</Dialog>

			{isLoading && (
				<Box sx={{ position: 'fixed', bottom: 16, right: 16 }}>
					<CircularProgress size={24} />
				</Box>
			)}
		</Box>
	);
};
